import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import API from '@scale/frontend-lib/dashboard-api';
import { Button } from 'design-system';
import { ChatHistory } from '@scale/shared/types/rlhf';
import _ from 'lodash';
import { TextCollectionAnnotation } from '@scale/shared/types/response/textcollection';
import { ReviewLevel } from '@scale/shared-types';

type LLMHelperContextType = {
  enabled: boolean;
  setEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  formatSections: (_sections: JSX.Element[]) => JSX.Element[];
  autoCritique: boolean;
  setAutoCritique: React.Dispatch<React.SetStateAction<boolean>>;
  autoSpec: boolean;
  setAutoSpec: React.Dispatch<React.SetStateAction<boolean>>;
  autoSpecText: string;
  setAutoSpecText: React.Dispatch<React.SetStateAction<string>>;
  autoSpecLoading: boolean;
  setAutoSpecLoading: React.Dispatch<React.SetStateAction<boolean>>;
  critiqueText: string;
  setCritiqueText: React.Dispatch<React.SetStateAction<string>>;
  critiqueResponseChanged: number;
  taskCustomer: string;
  setTaskCustomer: React.Dispatch<React.SetStateAction<string>>;
  setPromptVariables: React.Dispatch<React.SetStateAction<any>>;
  setAnnotations: (_annotations: Record<string, TextCollectionAnnotation>) => void;
  setAttachments: React.Dispatch<React.SetStateAction<any>>;
  isDone: boolean;
  aiAssistantEnabled: boolean;
  setAiAssistantEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  loadingAiAssistantPrompts: boolean;
  aiAssistantPrompts: any;
  getAiAssistantResponse: (prompt: string) => Promise<string>;
  activeIndices: Record<string, boolean>;
  setActiveIndices: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  attemptTexts: Record<string, string>;
  setAttemptTexts: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  responseNotDifferentEnough: string[];
  setResponseNotDifferentEnough: React.Dispatch<React.SetStateAction<string[]>>;
  setCurrentReviewLevel: React.Dispatch<React.SetStateAction<ReviewLevel | undefined>>;
  setIsReviewMode: React.Dispatch<React.SetStateAction<boolean>>;
};

const LLMHelperContext = React.createContext<LLMHelperContextType>({
  enabled: false,
  setEnabled: () => {},
  formatSections: (sections: JSX.Element[]) => sections,
  autoCritique: false,
  setAutoCritique: () => {},
  autoSpec: false,
  setAutoSpec: () => {},
  autoSpecText: '',
  setAutoSpecText: () => {},
  autoSpecLoading: false,
  setAutoSpecLoading: () => {},
  critiqueText: '',
  setCritiqueText: () => {},
  critiqueResponseChanged: 0,
  taskCustomer: '',
  setTaskCustomer: () => {},
  setPromptVariables: () => {},
  setAnnotations: (_annotations: Record<string, TextCollectionAnnotation>) => {},
  setAttachments: () => {},
  isDone: false,
  aiAssistantEnabled: false,
  setAiAssistantEnabled: () => {},
  loadingAiAssistantPrompts: false,
  aiAssistantPrompts: {},
  getAiAssistantResponse: async () => '',
  activeIndices: {},
  setActiveIndices: () => {},
  attemptTexts: {},
  setAttemptTexts: () => {},
  responseNotDifferentEnough: [],
  setResponseNotDifferentEnough: () => {},
  setCurrentReviewLevel: () => {},
  setIsReviewMode: () => {},
});

interface ProviderProps {
  projectId: string;
  children?: React.ReactNode;
}

const history: ChatHistory = { interactions: [] };

export const LLMHelperProvider: React.FC<ProviderProps> = ({ children, projectId }) => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [annotations, setAnnotations] = useState<Record<string, TextCollectionAnnotation>>({});
  const [attachments, setAttachments] = useState<any[]>([]);
  const [autoCritique, setAutoCritique] = useState<boolean>(false);
  const [autoSpec, setAutoSpec] = useState<boolean>(false);
  const [autoSpecText, setAutoSpecText] = useState<string>('');
  const [autoSpecLoading, setAutoSpecLoading] = useState<boolean>(false);
  const [critiquing, setCritiquing] = useState<boolean>(false);
  const [critiqueResponseChanged, setCritiqueResponseChanged] = useState<number>(0);
  const [critiqueText, setCritiqueText] = useState<string>('');
  const [critiqueBlockSubmit, setCritiqueBlockSubmit] = useState<boolean>(false);
  const [taskCustomer, setTaskCustomer] = useState<string>('');
  const [promptVariables, setPromptVariables] = useState<any>({});
  const [aiAssistantEnabled, setAiAssistantEnabled] = useState<boolean>(false);
  const [loadingAiAssistantPrompts, setLoadingAiAssistantPrompts] = useState<boolean>(false);
  const [aiAssistantPrompts, setAiAssistantPrompts] = useState<any>({});
  const [activeIndices, setActiveIndices] = useState<Record<string, boolean>>({}); // false for human attempt, true for gpt eval
  const [attemptTexts, setAttemptTexts] = useState<Record<string, string>>({});
  const [responseNotDifferentEnough, setResponseNotDifferentEnough] = useState<string[]>([]);
  const [currentReviewLevel, setCurrentReviewLevel] = useState<ReviewLevel | undefined>();
  const [isReviewMode, setIsReviewMode] = useState<boolean>(false);

  const formatSections = (sections: JSX.Element[]) => {
    if (autoSpec && autoSpecText.length && !autoSpecLoading) {
      sections.splice(
        0,
        0,
        <div>
          {!!autoSpecText.length && (
            <div
              style={{
                margin: '0 8px',
                padding: 8,
                whiteSpace: 'pre-wrap',
                backgroundColor: 'rgba(144, 202, 249, .2)',
                borderRadius: 8,
              }}
            >
              {autoSpecText}
            </div>
          )}
        </div>,
      );
    }
    if (autoCritique) {
      sections.splice(
        sections.length,
        0,
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Button
            disabled={critiquing || !critiqueResponseChanged}
            onClick={getAutoCritique}
            style={{ marginLeft: 'auto', marginRight: 16 }}
          >
            {critiquing ? 'Critiquing...' : 'Get Critique'}
          </Button>
        </div>,
      );
    }
    if (autoCritique && critiqueText.length) {
      const critical =
        critiqueText.toLowerCase().startsWith('severity: critical') ||
        critiqueText.toLowerCase().startsWith('severity: major');
      const rateLimitError = critiqueText.toLowerCase().includes('rate limit exceeded');
      sections.splice(
        sections.length - 1,
        0,
        <div
          style={{
            margin: '0 8px 8px 8px',
            padding: 8,
            whiteSpace: 'pre-wrap',
            backgroundColor: critical ? 'rgba(229, 115, 115, .2)' : 'rgba(255, 183, 77, .2)',
            borderRadius: 8,
          }}
        >
          {rateLimitError
            ? 'Critique unavailable due to too many concurrent requests'
            : critiqueText}
        </div>,
      );
    }
    return sections;
  };

  const getAutoSpec = useCallback(
    (promptBreakdown: any) => {
      (async () => {
        try {
          setAutoSpecLoading(true);
          const result = await API.selfserve.fetchAutoSpec({
            payload: {
              promptBreakdown,
              numCompletions: 1,
              projectId,
              history,
            },
            taskCustomer,
          });
          if (result.length) {
            setAutoSpecText(result);
          }
        } finally {
          setAutoSpecLoading(false);
        }
      })();
    },
    [projectId, taskCustomer],
  );

  useEffect(() => {
    if (autoSpec && !autoSpecText.length) {
      getAutoSpec(promptVariables);
    }
  }, [autoSpec, autoSpecText, getAutoSpec, promptVariables]);

  useEffect(() => {
    setCritiqueResponseChanged(p => p + 1);
  }, [annotations]);

  const getAutoCritique = () => {
    (async () => {
      setCritiquing(true);
      try {
        const result = await API.selfserve.fetchAutoCritique({
          payload: {
            promptBreakdown: promptVariables,
            numCompletions: 1,
            projectId,
            history: history.interactions ?? [],
          },
          taskCustomer,
        });
        if (result.length) {
          const text = result
            .split(/\r?\n|\r|\n/g)
            .filter((t: string) => !t.startsWith('Example response:'))
            .join('\n');
          setCritiqueText(text);
          setCritiqueBlockSubmit(true);
          setTimeout(() => setCritiqueBlockSubmit(false), 5000); // to ensure the crtique is read
        }
      } finally {
        setCritiqueResponseChanged(0);
        setCritiquing(false);
      }
    })();
  };

  useEffect(() => {
    const annotationsFormatted: Record<string, any> = _.cloneDeep(annotations);
    for (const key of Object.keys(annotations)) {
      annotationsFormatted[key] = Array.isArray(annotations[key].response)
        ? annotations[key].response.join()
        : annotations[key].response;
      if (Array.isArray(annotationsFormatted[key])) {
        annotationsFormatted[key] = annotationsFormatted[key].join();
      }
      annotationsFormatted[key] = `${annotationsFormatted[key]}`;
    }
    const attachmentsFormatted: any = {};
    for (const [i, attachment] of attachments.entries()) {
      attachmentsFormatted[`attachment_${i + 1}`] = attachment.content ?? '';
    }
    setPromptVariables({ ...attachmentsFormatted, ...annotationsFormatted });
  }, [annotations, attachments]);

  const isDone = useMemo(() => {
    if (autoCritique && !critiqueText.length) {
      return false;
    }

    if (autoCritique && critiqueBlockSubmit) {
      return false;
    }
    return true;
  }, [autoCritique, critiqueBlockSubmit, critiqueText.length]);

  useEffect(() => {
    if (aiAssistantEnabled) {
      (async () => {
        try {
          setLoadingAiAssistantPrompts(true);
          const result = await API.selfserve.fetchAiAssistantPrompts(projectId);
          if (typeof currentReviewLevel === 'number') {
            for (const fieldId of Object.keys(result)) {
              // if this is review mode, and this is enabled for L0, do not delete
              if (
                isReviewMode &&
                result[fieldId].reviewLevels?.length &&
                result[fieldId].reviewLevels.includes(0)
              ) {
                continue;
              }

              if (
                result[fieldId].reviewLevels?.length &&
                !result[fieldId].reviewLevels.includes(currentReviewLevel)
              ) {
                delete result[fieldId];
              }
            }
          }
          if (Object.keys(result).length) setAiAssistantPrompts(result);
        } finally {
          setLoadingAiAssistantPrompts(false);
        }
      })();
    }
  }, [aiAssistantEnabled, projectId, currentReviewLevel]);

  const getAiAssistantResponse = async (field_id: string) => {
    let promptSystem = aiAssistantPrompts[field_id].system;
    let promptUser = aiAssistantPrompts[field_id].user;
    if (promptSystem && promptUser) {
      for (const [i, attachment] of attachments.entries()) {
        promptSystem = promptSystem.replace(`{{attachment_${i + 1}}}`, attachment.content ?? '');
        promptUser = promptUser.replace(`{{attachment_${i + 1}}}`, attachment.content ?? '');
      }
      const { content } = await API.selfserve.askGPT({
        promptSystem,
        promptUser,
        options: { model: 'gpt-4' },
      });
      return content;
    }
    return '';
  };

  return (
    <LLMHelperContext.Provider
      value={{
        enabled,
        setEnabled,
        formatSections,
        autoCritique,
        setAutoCritique,
        autoSpec,
        setAutoSpec,
        autoSpecText,
        setAutoSpecText,
        autoSpecLoading,
        setAutoSpecLoading,
        critiqueText,
        setCritiqueText,
        critiqueResponseChanged,
        taskCustomer,
        setTaskCustomer,
        setPromptVariables,
        setAnnotations,
        setAttachments,
        isDone,
        aiAssistantEnabled,
        setAiAssistantEnabled,
        loadingAiAssistantPrompts,
        aiAssistantPrompts,
        getAiAssistantResponse,
        activeIndices,
        setActiveIndices,
        attemptTexts,
        setAttemptTexts,
        responseNotDifferentEnough,
        setResponseNotDifferentEnough,
        setCurrentReviewLevel,
        setIsReviewMode,
      }}
    >
      {children}
    </LLMHelperContext.Provider>
  );
};

export const useLLMHelper = () => useContext(LLMHelperContext);
